import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Login({ onLogin }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: formData.email,
        password: formData.password,
      });
      if (response.data.success) {
        const { token, user, mustChangePassword } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        if (onLogin) onLogin(user);
        // Redirect to password change page if required
        navigate(mustChangePassword ? '/change-password' : '/home');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = provider => {
    window.location.href = `${API_URL}/auth/oauth/${provider}`;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="row h-100 align-items-center g-0">
          {/* Left Side — Branding */}
          <div className="col-lg-6 d-none d-lg-flex login-brand-section">
            <div className="brand-content">
              <div className="brand-logo mb-4">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="64" height="64" rx="12" fill="white" opacity="0.1" />
                  <path d="M32 16C23.2 16 16 23.2 16 32C16 40.8 23.2 48 32 48C40.8 48 48 40.8 48 32C48 23.2 40.8 16 32 16ZM28.4 38.4L22.4 32.4L24.4 30.4L28.4 34.4L39.6 23.2L41.6 25.2L28.4 38.4Z" fill="white" />
                </svg>
              </div>
              <h1 className="brand-title">ZipQA</h1>
              <p className="brand-subtitle">Modern Test Management Platform</p>
              <div className="brand-features mt-5">
                <div className="brand-feature"><span className="feature-check">✓</span><span>Comprehensive test case management</span></div>
                <div className="brand-feature"><span className="feature-check">✓</span><span>Real-time test execution tracking</span></div>
                <div className="brand-feature"><span className="feature-check">✓</span><span>Seamless CI/CD integration</span></div>
                <div className="brand-feature"><span className="feature-check">✓</span><span>Powerful reporting & analytics</span></div>
              </div>
            </div>
          </div>

          {/* Right Side — Sign In Form */}
          <div className="col-lg-6 login-form-section">
            <div className="form-wrapper">
              <div className="form-header">
                <h2 className="form-title">Welcome Back</h2>
                <p className="form-subtitle">Sign in to access your test management dashboard</p>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <svg className="me-2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter password"
                  />
                  <div className="text-end mt-1">
                    <Link to="/forgot-password" style={{ fontSize: 13, color: '#6366f1', textDecoration: 'none' }}>
                      Forgot password?
                    </Link>
                  </div>
                </div>
                <button type="submit" className="btn btn-login w-100 mb-4" disabled={loading}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Signing In…</>
                    : '🚀 Sign In'}
                </button>
              </form>

              <div className="divider"><span>OR</span></div>

              <div className="oauth-buttons">
                <button className="btn btn-oauth google" onClick={() => handleOAuthLogin('google')} type="button" disabled={loading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Google
                </button>
                <button className="btn btn-oauth microsoft" onClick={() => handleOAuthLogin('azure')} type="button" disabled={loading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
                  </svg>
                  Microsoft
                </button>
              </div>

              <div className="form-footer">
                <p className="text-muted small text-center mb-0" style={{ marginTop: '1.5rem' }}>
                  🔒 Access is by invitation only. Contact your admin to get an account.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
